import { HttpErrorResponse, HttpInterceptorFn } from '@angular/common/http';
import { catchError, switchMap, throwError } from 'rxjs';
import { AuthService } from '../services/auth.service';
import { SessionExpiryService } from '../services/session-expiry.service';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { httpErrorIndicatesJwtAuthFailure } from '../utils/auth-http-error';

/**
 * On JWT-related 401/403, attempts ONE deduped refresh via httpOnly
 * `refreshToken` cookie (`POST /user/refresh`, withCredentials). Does not
 * refresh on RBAC denial (Shield code 1004).
 *
 * Refresh failure → clear client auth and send the user to /welcome.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const authService = inject(AuthService);
  const sessionExpiry = inject(SessionExpiryService);
  const router = inject(Router);

  const buildReq = () => {
    const token = authService.getToken();
    const hasBearer = typeof token === 'string' && token.trim().length > 0;
    const headers = req.headers.set('Accept', 'application/json');

    return req.clone({
      headers: hasBearer
        ? headers.set('Authorization', `Bearer ${token}`)
        : headers.delete('Authorization'),
      withCredentials: true,
    });
  };

  const isAuthPage = () => {
    const url = (router.url || '').split(/[?#]/)[0];
    return (
      url.startsWith('/welcome') ||
      url.startsWith('/admin-login') ||
      url.startsWith('/nurse-login') ||
      url.startsWith('/forgot-password') ||
      url.startsWith('/reset-password')
    );
  };

  const forceWelcome = () => {
    sessionExpiry.reset();
    authService.clearClientAuthStateWithoutServerCall();
    if (!isAuthPage()) {
      void router.navigateByUrl('/welcome', { replaceUrl: true });
    }
  };

  return next(buildReq()).pipe(
    catchError((error: HttpErrorResponse) => {
      const reqUrl = typeof req.url === 'string' ? req.url : '';

      // Never try to refresh the refresh call itself (prevents loops).
      if (/\b(user|nurse|client)\/refresh\b/i.test(reqUrl)) {
        return throwError(() => error);
      }

      // Login / password flows should surface their own errors.
      if (/\b(user|nurse|client)\/(login|forgot|reset)\b/i.test(reqUrl)) {
        return throwError(() => error);
      }

      const isJwtChallenge = httpErrorIndicatesJwtAuthFailure(error);
      if (!isJwtChallenge) {
        return throwError(() => error);
      }

      /**
       * Always attempt cookie-based refresh on JWT failure — the httpOnly
       * refreshToken cookie may still be valid even if access token in
       * localStorage is missing/expired. SessionIntegrityService handles
       * fully wiped storage separately.
       */
      return authService.refreshSession().pipe(
        switchMap(() => {
          sessionExpiry.reset();
          return next(buildReq());
        }),
        catchError((refreshError) => {
          sessionExpiry.markExpired(router.url);
          forceWelcome();
          return throwError(() => refreshError ?? error);
        }),
      );
    }),
  );
};
